import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Modal, Input, Button, Select } from "../../../components/ui";

import { useAppMutation, useAppQuery } from "../../../api/fetchClient";
import { ADD_HOSPITAL, GET_COUNTRY_LIST, UPDATE_HOSPITAL } from "../../../api/url";
import { strings } from "../../../strings/strings";
import { QUERY_KEYS } from "../../../api/queryKeys";

const emptyValues = {
  name: "",
  referral_code: "",
  address: "",
  city: "",
  state: "",
  hospital_country: "",
  mobile_code: "",
  mobile_number: "",
  email: "",
};
const normalize = (v) => (typeof v === "string" ? v.trim() : v);
const buildPayload = (values) => {
  const payload = {};

  Object.entries(values).forEach(([key, value]) => {
    const v = normalize(value ?? "");
    if (v !== "") payload[key] = v;
  });

  if (!payload.mobile_number) delete payload.mobile_code;

  return payload;
};

const buildChangedPayload = (initial, current) => {
  const changed = {};
  const allKeys = new Set([...Object.keys(initial || {}), ...Object.keys(current || {})]);
  allKeys.forEach((key) => {
    const a = normalize(initial?.[key] ?? "");
    const b = normalize(current?.[key] ?? "");
    if (a !== b) changed[key] = b;
  });
  return changed;
};

const AddHospitalModal = ({ isOpen, onClose, onSuccess, mode = "add", hospital = null }) => {
  const currentValidationSchema = useMemo(() => Yup.object({
    name: Yup.string().trim().required(strings.hospitalRequired),

    referral_code: mode === "edit"
      ? Yup.string().trim()
      : Yup.string()
          .trim()
          .required(strings.referralCodeIsRequired)
          .matches(/^\d{4}$/, strings.referralCodeMustBe),

    address: Yup.string().trim(),
    city: Yup.string().trim(),
    state: Yup.string().trim(),

    hospital_country: mode === "edit"
      ? Yup.string().trim()
      : Yup.string().trim().required("Country is required"),
  }), [mode]);
  
  const mutation = useAppMutation();
  const queryClient = useQueryClient();
  const { data: countryRes, isLoading: isCountryLoading } = useAppQuery({
    queryKey: [QUERY_KEYS.COUNTRY],
    url: GET_COUNTRY_LIST,
    method: "GET",
    enabled: isOpen,
  });
  const countries = countryRes?.results?.data || countryRes?.results || countryRes?.data || [];

const countryOptions = (countries || []).map((c) => ({
  label: c.name || c.country_name || "Unknown",
  value: c.name || c.country_name || "",
}));

  const editInitialValues = useMemo(() => {
    if (mode !== "edit" || !hospital) return emptyValues;

    return {
      name: hospital?.name || "",
      referral_code: hospital?.referral_code || "",
      address: hospital?.address || "",
      city: hospital?.city || "",
      state: hospital?.state || "",
      hospital_country: String(hospital?.hospital_country || ""),

    };
  }, [mode, hospital]);

  const formik = useFormik({
    initialValues: emptyValues,
    validationSchema: currentValidationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: false,

    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const isEdit = mode === "edit";
        const hospitalId = hospital?.hospitalId ?? hospital?.id ?? hospital?._id;

        if (isEdit && !hospitalId) {
          toast.error(strings.hospitalIdNotFound);
          return;
        }

        const fullPayload = buildPayload(values);
        if (!isEdit) {
          const res = await mutation.mutateAsync({
            url: ADD_HOSPITAL,
            method: "POST",
            data: fullPayload,
          });

         
          await queryClient.invalidateQueries({ 
            queryKey: [QUERY_KEYS.HOSPITAL],
            exact: false 
          });
          
          toast.success(res?.message);
          resetForm();
          onClose?.();
          onSuccess?.();
          return;
        }
        const changedPayload = buildChangedPayload(editInitialValues, fullPayload);

        if (Object.keys(changedPayload).length === 0) {
          toast.info(strings.noChangesToUpdate);
          return;
        }

        const res = await mutation.mutateAsync({
          url: `${UPDATE_HOSPITAL}?hospitalId=${hospitalId}`,
          method: "PUT",
          data: changedPayload,
        });
        await queryClient.invalidateQueries({ 
          queryKey: [QUERY_KEYS.HOSPITAL],
          exact: false
        });
        
        toast.success(res?.message);
        resetForm();
        onClose?.();
        onSuccess?.();
      } catch (error) {
        toast.error(error?.message);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit" && hospital) {
      formik.setValues(editInitialValues);
      formik.setTouched({});
      formik.setErrors({});
    } else {
      formik.resetForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, hospital, editInitialValues]);

  const handleClose = () => {
    formik.resetForm();
    onClose?.();
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <Button type="button" variant="outline" onClick={handleClose} disabled={formik.isSubmitting}>
        {strings.cancel}
      </Button>

      <Button
        type="submit"
        variant="primary"
        isLoading={formik.isSubmitting}
        disabled={formik.isSubmitting || !formik.isValid}
        onClick={formik.handleSubmit}
      >
        {mode === strings.edit2 ? strings.update : strings.save}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === strings.edit2 ? strings.editHospital : strings.addHospital}
      footer={footer}
      size="xl"
      className="overflow-visible"
    >
      <form onSubmit={formik.handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Hospital Name"
            name="name"
            required
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.name}
            touched={formik.touched.name}
            placeholder="Enter Hospital Name"
          />

          <Input
            label="Referral Code"
            name="referral_code"
            required
            value={formik.values.referral_code}
            onChange={(e) => {
              const onlyDigits = e.target.value.replace(/[^\d]/g, "");
              formik.setFieldValue("referral_code", onlyDigits.slice(0, 4));
            }}
            onBlur={formik.handleBlur}
            error={formik.errors.referral_code}
            touched={formik.touched.referral_code}
            placeholder={strings.refferalCodePlaceholder}
            inputMode="numeric"
            maxLength={4}
            disabled={mode === "edit"}
            className={mode === "edit" ? "bg-gray-100 text-gray-500 cursor-not-allowed opacity-65" : ""}
          />

          <div className="md:col-span-2">
            <Input
              label="Address"
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.address}
              touched={formik.touched.address}
              placeholder="Enter Address"
            />
          </div>

          <Input
            label="City"
            name="city"
            value={formik.values.city}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.city}
            touched={formik.touched.city}
            placeholder="Enter City Name"
          />

          <Input
            label="State"
            name="state"
            value={formik.values.state}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.state}
            touched={formik.touched.state}
            placeholder="Enter State Name"
          />

          {mode === "edit" ? (
            <Input
              label="Country"
              name="hospital_country"
              required
              value={hospital?.country || "-"}
              disabled={true}
              className="bg-gray-100 text-gray-500 cursor-not-allowed opacity-65"
            />
          ) : (
            <Select
              label="Country"
              name="hospital_country"
              required
              placeholder={isCountryLoading ? "Loading..." : "Select Country"}
              value={formik.values.hospital_country}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.hospital_country}
              touched={formik.touched.hospital_country}
              options={countryOptions}
              disabled={isCountryLoading}
              dropdownDirection="up"
            />
          )}
        </div>
      </form>
    </Modal>
  );
};

export default AddHospitalModal;
